import React, { createContext, useContext, useCallback, useRef, useState } from 'react';
import { db } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const FarmDataContext = createContext(null);

export function FarmDataProvider({ children }) {
  const cacheRef = useRef({});
  const inflightRef = useRef({});
  const [, setTick] = useState(0);

  const get = useCallback((type, key) => {
    return cacheRef.current[`${type}::${key}`]?.data ?? null;
  }, []);

  const set = useCallback((type, key, data) => {
    cacheRef.current[`${type}::${key}`] = { data, ts: Date.now() };
    setTick(t => t + 1);
  }, []);

  const fetchIfNeeded = useCallback(async (type, key, fetcher) => {
    const cacheKey = `${type}::${key}`;
    const existing = cacheRef.current[cacheKey];
    if (existing?.data) return existing.data;

    if (inflightRef.current[cacheKey]) {
      return inflightRef.current[cacheKey];
    }

    const promise = fetcher().then(data => {
      cacheRef.current[cacheKey] = { data, ts: Date.now() };
      delete inflightRef.current[cacheKey];
      setTick(t => t + 1);
      return data;
    }).catch(err => {
      delete inflightRef.current[cacheKey];
      throw err;
    });

    inflightRef.current[cacheKey] = promise;
    return promise;
  }, []);

  const fetchWithFirestore = useCallback(async (type, key, firestorePath, apiFetcher) => {
    const cacheKey = `${type}::${key}`;

    const existing = cacheRef.current[cacheKey];
    if (existing?.data) return existing.data;

    if (inflightRef.current[cacheKey]) {
      return inflightRef.current[cacheKey];
    }

    const promise = (async () => {
      try {
        const ref = doc(db, ...firestorePath);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const fsData = snap.data()?.payload ?? snap.data();
          if (fsData && Object.keys(fsData).length > 0) {
            const isStale = type === 'weeklyScores' && fsData.weeks?.length > 0 && !fsData.weeks[0].raw;
            if (!isStale) {
              cacheRef.current[cacheKey] = { data: fsData, ts: Date.now() };
              setTick(t => t + 1);
              return fsData;
            }
          }
        }
      } catch (_) {}

      const data = await apiFetcher();
      cacheRef.current[cacheKey] = { data, ts: Date.now() };
      setTick(t => t + 1);

      try {
        const ref = doc(db, ...firestorePath);
        await setDoc(ref, { payload: data, cachedAt: Date.now() }, { merge: true });
      } catch (_) {}

      return data;
    })();

    inflightRef.current[cacheKey] = promise;
    promise.then(() => { delete inflightRef.current[cacheKey]; }).catch(() => { delete inflightRef.current[cacheKey]; });
    return promise;
  }, []);

  const invalidate = useCallback((type, key) => {
    if (key) {
      delete cacheRef.current[`${type}::${key}`];
    } else {
      const prefix = `${type}::`;
      for (const k of Object.keys(cacheRef.current)) {
        if (k.startsWith(prefix)) delete cacheRef.current[k];
      }
    }
    setTick(t => t + 1);
  }, []);

  const invalidateAll = useCallback(() => {
    cacheRef.current = {};
    setTick(t => t + 1);
  }, []);

  return (
    <FarmDataContext.Provider value={{ get, set, fetchIfNeeded, fetchWithFirestore, invalidate, invalidateAll }}>
      {children}
    </FarmDataContext.Provider>
  );
}

export function useFarmData() {
  const ctx = useContext(FarmDataContext);
  if (!ctx) throw new Error('useFarmData must be used within FarmDataProvider');
  return ctx;
}
